import { useAppSelector } from '@hooks/redux.hooks';
import { selectPermissions } from '@redux/authent/authent.selectors';
import { ReactNode } from 'react';
import { Navigate, useParams } from 'react-router';

import { RoutesEnum } from '@/types/routes.types';

import { CockpitPanelIntlKeysEnum } from './cockpit.types';
import { FlightPlanManagementPanel } from './panels/flightPlan/FlightPlans';
import LogbookAndChat from './panels/logbookAndChat/LogbookAndChat';
import MonitoringPanel from './panels/monitoring/MonitoringPanel';
import NotificationsPanel from './panels/notification/NotificationsPanel';
import { NotificationSearchPanel } from './panels/notificationSearch/NotificationSearchPanel';
import OperatorStationConfiguration from './panels/operatorStationConfiguration/OperatorStationConfiguration';
import { OrganizationManagement } from './panels/organization/Organizations';
import { PlatformManagement } from './panels/platformManagement/PlatformManagement';
import SensorInstallationPanel from './panels/sensorInstallation/SensorInstallationPanel';
import ProfileUserParams from './panels/settings/ProfileUserParams';
import { SystemParametersPanel } from './panels/systemParameters/SystemParametersPanel';
import UsersManagement from './panels/usersManagement/UsersManagement';
import UtmManagement from './panels/utm/UtmManagement';
import { ZoneManagement } from './panels/zoneManagement/ZoneManagement';

export const PANEL_ROUTES: Record<CockpitPanelIntlKeysEnum, string> = {
  NOTIFICATION: 'notifications',
  LOG_BOOK_AND_CHAT: 'logbook-chat',
  OPERATOR_STATION_CONFIGURATION: 'operator-station',
  SENSOR_INSTALLATION: 'sensor-installation',
  SETTINGS: 'settings',
  ORGANIZATION: 'organizations',
  PLATFORM: 'platforms',
  ZONE: 'zones',
  USERS_MANAGEMENT: 'users',
  MONITORING: 'monitoring',
  NOTIFICATION_SEARCH: 'notification-search',
  SYSTEM_PARAMETERS: 'system-parameters',
  DRONES_FLEET: 'drone-fleet',
  FLIGHT_PLANS: 'flight-plans',
};

type RequirePermissionProps = {
  panel: CockpitPanelIntlKeysEnum;
  children: ReactNode;
};

function RequirePermission({ panel, children }: Readonly<RequirePermissionProps>) {
  const permissions = useAppSelector(selectPermissions);
  if (!permissions[panel]) {
    return <Navigate to={RoutesEnum.COCKPIT} replace />;
  }

  return children;
}

export const cockpitPanels = [
  {
    path: PANEL_ROUTES.NOTIFICATION,
    element: (
      <RequirePermission panel={CockpitPanelIntlKeysEnum.NOTIFICATION}>
        <NotificationsPanel />
      </RequirePermission>
    ),
  },
  {
    path: PANEL_ROUTES.LOG_BOOK_AND_CHAT,
    element: (
      <RequirePermission panel={CockpitPanelIntlKeysEnum.LOG_BOOK_AND_CHAT}>
        <LogbookAndChat />
      </RequirePermission>
    ),
  },
  {
    path: PANEL_ROUTES.MONITORING,
    element: (
      <RequirePermission panel={CockpitPanelIntlKeysEnum.MONITORING}>
        <MonitoringPanel />
      </RequirePermission>
    ),
  },
  {
    path: PANEL_ROUTES.DRONES_FLEET,
    element: (
      <RequirePermission panel={CockpitPanelIntlKeysEnum.DRONES_FLEET}>
        <UtmManagement />
      </RequirePermission>
    ),
  },
  {
    path: PANEL_ROUTES.FLIGHT_PLANS,
    element: (
      <RequirePermission panel={CockpitPanelIntlKeysEnum.FLIGHT_PLANS}>
        <FlightPlanManagementPanel />
      </RequirePermission>
    ),
  },
  {
    path: PANEL_ROUTES.NOTIFICATION_SEARCH,
    element: (
      <RequirePermission panel={CockpitPanelIntlKeysEnum.NOTIFICATION_SEARCH}>
        <NotificationSearchPanel />
      </RequirePermission>
    ),
  },
  {
    path: PANEL_ROUTES.SENSOR_INSTALLATION,
    element: (
      <RequirePermission panel={CockpitPanelIntlKeysEnum.SENSOR_INSTALLATION}>
        <SensorInstallationPanel />
      </RequirePermission>
    ),
  },
  {
    path: PANEL_ROUTES.ZONE,
    element: (
      <RequirePermission panel={CockpitPanelIntlKeysEnum.ZONE}>
        <ZoneManagement />
      </RequirePermission>
    ),
  },
  {
    path: PANEL_ROUTES.ORGANIZATION,
    element: (
      <RequirePermission panel={CockpitPanelIntlKeysEnum.ORGANIZATION}>
        <OrganizationManagement />
      </RequirePermission>
    ),
  },
  {
    path: PANEL_ROUTES.USERS_MANAGEMENT,
    element: (
      <RequirePermission panel={CockpitPanelIntlKeysEnum.USERS_MANAGEMENT}>
        <UsersManagement />
      </RequirePermission>
    ),
  },
  {
    path: PANEL_ROUTES.PLATFORM,
    element: (
      <RequirePermission panel={CockpitPanelIntlKeysEnum.PLATFORM}>
        <PlatformManagement />
      </RequirePermission>
    ),
  },
  {
    path: PANEL_ROUTES.OPERATOR_STATION_CONFIGURATION,
    element: (
      <RequirePermission panel={CockpitPanelIntlKeysEnum.OPERATOR_STATION_CONFIGURATION}>
        <OperatorStationConfiguration />
      </RequirePermission>
    ),
  },
  {
    path: PANEL_ROUTES.SETTINGS,
    element: <ProfileUserParams />,
  },
  {
    path: PANEL_ROUTES.SYSTEM_PARAMETERS,
    element: <SystemParametersPanel />,
  },
];

export function CockpitPanelRouter() {
  const { panel } = useParams();

  const match = cockpitPanels.find((p) => p.path === panel);

  if (!match) {
    return <Navigate to="notifications" replace />;
  }

  return match.element;
}
